<script lang="ts">
  import { lobbyStore, clearLobby } from '../stores/lobbyStore';
  import { playerStore, clearPlayer } from '../stores/playerStore';
  import { socketService } from '../services/socketService';
  import { clearPlayerIdentity } from '../lib/storage';
  import { announceToScreenReader } from '../lib/accessibility';
  import { PlayerStatus, LobbyStatus } from '@blather/shared';

  $: lobby = $lobbyStore.lobby;
  $: currentPlayer = $playerStore.player;
  $: activePlayers = lobby?.players.filter((p) => p.status === PlayerStatus.CONNECTED) || [];
  $: disconnectedPlayers = lobby?.players.filter((p) => p.status === PlayerStatus.DISCONNECTED) || [];
  $: audienceMembers = lobby?.audience || [];

  // Host is the first player in the lobby
  $: isHost = lobby && currentPlayer && lobby.players[0]?.id === currentPlayer.id;
  $: canStartGame = activePlayers.length >= 3;
  $: isPlayer = currentPlayer?.role === 'player';

  // Game configuration state
  let timerMultiplierInput = '1.0';
  let roundLimitInput = '';
  let scoreLimitInput = '';
  let configError = '';

  function leaveLobby() {
    const socket = socketService.getSocket();
    if (socket) {
      socket.emit('lobby:leave');
    }
    clearPlayerIdentity();
    clearPlayer();
    clearLobby();
    announceToScreenReader('Left lobby');
  }

  function validateAndStartGame() {
    if (!canStartGame) {
      announceToScreenReader(`Need ${3 - activePlayers.length} more player(s) to start`, 'assertive');
      return;
    }

    configError = '';

    // Validate timer multiplier
    const timerMultiplier = parseFloat(timerMultiplierInput);
    if (isNaN(timerMultiplier) || timerMultiplier <= 0) {
      configError = 'Timer multiplier must be a positive number';
      announceToScreenReader(configError, 'assertive');
      return;
    }

    // Validate round limit (optional)
    let roundLimit = null;
    if (roundLimitInput.trim()) {
      roundLimit = parseInt(roundLimitInput);
      if (isNaN(roundLimit) || roundLimit < 1) {
        configError = 'Round limit must be a positive integer or empty';
        announceToScreenReader(configError, 'assertive');
        return;
      }
    }

    // Validate score limit (optional)
    let scoreLimit = null;
    if (scoreLimitInput.trim()) {
      scoreLimit = parseInt(scoreLimitInput);
      if (isNaN(scoreLimit) || scoreLimit < 1) {
        configError = 'Score limit must be a positive integer or empty';
        announceToScreenReader(configError, 'assertive');
        return;
      }
    }

    const socket = socketService.getSocket();
    if (socket) {
      socket.emit('game:start', {
        timerMultiplier,
        roundLimit,
        scoreLimit,
      });
      announceToScreenReader('Starting game');
    }
  }
</script>

<div class="waiting-room" role="main">
  {#if lobby}
    <header>
      <h1>Lobby: {lobby.id}</h1>
      <p class="status-text">Status: {lobby.status.replace('_', ' ')}</p>
    </header>

    <section aria-labelledby="players-heading">
      <h2 id="players-heading">Players ({activePlayers.length})</h2>
      {#if activePlayers.length > 0}
        <ul role="list" class="player-list">
          {#each activePlayers as player (player.id)}
            <li>
              <span class="player-name">{player.username}</span>
              {#if player.id === currentPlayer?.id}
                <span class="badge" aria-label="You">You</span>
              {/if}
            </li>
          {/each}
        </ul>
      {:else}
        <p class="empty-state">No active players</p>
      {/if}
    </section>

    {#if disconnectedPlayers.length > 0}
      <section aria-labelledby="disconnected-heading">
        <h2 id="disconnected-heading">Disconnected Players ({disconnectedPlayers.length})</h2>
        <ul role="list" class="player-list">
          {#each disconnectedPlayers as player (player.id)}
            <li class="disconnected">
              <span class="player-name">{player.username}</span>
              <span class="status-badge">Disconnected</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    {#if audienceMembers.length > 0}
      <section aria-labelledby="audience-heading">
        <h2 id="audience-heading">Audience ({audienceMembers.length})</h2>
        <ul role="list" class="player-list">
          {#each audienceMembers as spectator (spectator.id)}
            <li class="audience">
              <span class="player-name">{spectator.username}</span>
              <span class="role-badge">Spectator</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    {#if lobby.status === LobbyStatus.WAITING && isPlayer && isHost}
      <section class="game-config" aria-labelledby="config-heading">
        <h2 id="config-heading">Game Settings</h2>

        <div class="config-field">
          <label for="timer-multiplier">
            Timer Speed
            <span class="help-text">1.0 = normal, 0.5 = half speed, 2.0 = double speed</span>
          </label>
          <input
            id="timer-multiplier"
            type="text"
            bind:value={timerMultiplierInput}
            placeholder="1.0"
            aria-describedby="timer-help"
          />
          <span id="timer-help" class="sr-only">Enter a positive number for timer multiplier</span>
        </div>

        <div class="config-field">
          <label for="round-limit">
            Round Limit
            <span class="help-text">Leave empty for no limit</span>
          </label>
          <input
            id="round-limit"
            type="text"
            bind:value={roundLimitInput}
            placeholder="No limit"
            aria-describedby="round-help"
          />
          <span id="round-help" class="sr-only">Enter a positive integer or leave empty for no round limit</span>
        </div>

        <div class="config-field">
          <label for="score-limit">
            Score Limit
            <span class="help-text">Leave empty for no limit</span>
          </label>
          <input
            id="score-limit"
            type="text"
            bind:value={scoreLimitInput}
            placeholder="No limit"
            aria-describedby="score-help"
          />
          <span id="score-help" class="sr-only">Enter a positive integer or leave empty for no score limit</span>
        </div>

        {#if configError}
          <div class="config-error" role="alert">
            {configError}
          </div>
        {/if}
      </section>
    {/if}

    <div class="actions">
      {#if lobby.status === LobbyStatus.WAITING && isPlayer}
        {#if isHost}
          <button
            on:click={validateAndStartGame}
            class="primary-button"
            disabled={!canStartGame}
            aria-label={canStartGame ? 'Start game' : `Need ${3 - activePlayers.length} more player(s) to start`}
          >
            Start Game
          </button>
        {:else}
          <button
            class="primary-button disabled-button"
            disabled
            aria-label="Waiting for host to start game"
          >
            Waiting for host...
          </button>
        {/if}
      {/if}
      <button on:click={leaveLobby} class="secondary-button">
        Leave Lobby
      </button>
    </div>
  {:else}
    <p>Loading lobby...</p>
  {/if}
</div>

<style>
  .waiting-room {
    max-width: 700px;
    margin: 2rem auto;
    padding: 2rem;
  }

  header {
    margin-bottom: 2rem;
  }

  h1 {
    margin-bottom: 0.5rem;
  }

  .status-text {
    font-size: 1rem;
    color: var(--color-text-secondary);
    text-transform: capitalize;
  }

  section {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background-color: var(--color-background-alt);
    border-radius: var(--border-radius);
  }

  h2 {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .player-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .player-list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background-color: var(--color-background);
    border-radius: var(--border-radius);
    border: 1px solid var(--color-border);
  }

  .player-name {
    font-weight: 500;
  }

  .disconnected {
    opacity: 0.6;
  }

  .disconnected .player-name {
    text-decoration: line-through;
  }

  .status-badge,
  .role-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    background-color: var(--color-warning);
    color: white;
    font-weight: 600;
  }

  .role-badge {
    background-color: var(--color-text-secondary);
  }

  .empty-state {
    color: var(--color-text-secondary);
    font-style: italic;
  }

  .actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
  }

  .primary-button {
    flex: 1;
    background-color: var(--color-success);
  }

  .primary-button:hover:not(:disabled) {
    background-color: #218838;
  }

  .primary-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: var(--color-text-secondary);
  }

  .disabled-button {
    background-color: var(--color-text-secondary) !important;
  }

  .secondary-button {
    flex: 1;
    background-color: var(--color-text-secondary);
  }

  .secondary-button:hover:not(:disabled) {
    background-color: #555555;
  }

  /* Game configuration styles */
  .game-config {
    background-color: var(--color-background-alt);
    padding: 1.5rem;
    border-radius: var(--border-radius);
    margin-bottom: 2rem;
  }

  .config-field {
    margin-bottom: 1.25rem;
  }

  .config-field:last-of-type {
    margin-bottom: 0;
  }

  .config-field label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: var(--color-text);
  }

  .help-text {
    display: block;
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--color-text-secondary);
    margin-top: 0.25rem;
  }

  .config-field input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: 1rem;
    background-color: var(--color-background);
    color: var(--color-text);
  }

  .config-field input:focus {
    outline: none;
    border-color: var(--color-success);
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
  }

  .config-error {
    padding: 0.75rem 1rem;
    background-color: #fee;
    border: 1px solid #fcc;
    border-radius: var(--border-radius);
    color: #c33;
    font-weight: 600;
    margin-top: 1rem;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  @media (max-width: 600px) {
    .waiting-room {
      padding: 1rem;
      margin: 1rem;
    }

    .actions {
      flex-direction: column;
    }
  }
</style>
